<template>
  <div class="spaces-field-block">
    <div class="flex justify-content-between flex-wrap">
      <div class="flex justify-content-center align-items-center">
        <label class="block my-3">
          {{ t(configs.label) }}
        </label>
      </div>
      <div class="flex justify-content-center align-items-center gap-2">
        <ToggleDisplaySettings
          v-if="!Object.prototype.hasOwnProperty.call(configs, 'displayToggle') || configs.displayToggle"
          :configs="configs"
          :parser-values="parserValues"
          :element-part-type="elementPartType"
          @display-settings="(value) => displayProperties = value"
        />
      </div>
    </div>
    <div
      :class="{ 'hidden': !displayProperties }"
    >
      <!-- Mode Simple: 1 seul input -->
      <div
        v-if="!advancedMode"
        class="simple-mode"
      >
        <BaseInputNumber
          v-model="unifiedValue"
          data-test-id="input-element-unified"
          :suffix="SIGN"
          input-width="3.8rem"
          @input="handleUnifiedInput"
        />
        <Button
          v-if="inputList.length > 1"
          v-tooltip.top="t('templateBuilder.fields.spaces.advancedMode')"
          class="p-button-text p-button-sm advanced-toggle-btn"
          @click="toggleAdvancedMode"
        >
          <i class="far fa-sliders-h" />
        </Button>
      </div>

      <!-- Mode Avancé: disposition spatiale -->
      <div
        v-else
        class="advanced-mode"
      >
        <div class="advanced-mode-header">
          <Button
            v-tooltip.top="t('templateBuilder.fields.spaces.simpleMode')"
            class="p-button-text p-button-sm advanced-toggle-btn"
            @click="toggleAdvancedMode"
          >
            <i class="far fa-compress-alt" />
          </Button>
        </div>
        <!-- Layout 4 valeurs pour border-radius (coins) -->
        <div
          v-if="inputList.length === 4 && isBorderRadius"
          class="spatial-layout-corners"
        >
          <!-- Ligne du haut: top-left et top-right -->
          <div class="corners-row">
            <BaseInputNumber
              v-model="topInput.value"
              data-test-id="input-element-top-left"
              :suffix="SIGN"
              input-width="3.8rem"
              @input="handleInput(topInput, topInput.index)"
            />
            <BaseInputNumber
              v-model="rightInput.value"
              data-test-id="input-element-top-right"
              :suffix="SIGN"
              input-width="3.8rem"
              @input="handleInput(rightInput, rightInput.index)"
            />
          </div>
          <!-- Cadenas central -->
          <div class="corners-center">
            <Button
              v-tooltip.top="allLocked ? t('templateBuilder.fields.spaces.unlockValues') : t('templateBuilder.fields.spaces.lockValues')"
              data-test-id="lock-button-central"
              class="lock-button-central"
              :class="{ 'locked': allLocked }"
              @click="toggleAllLocked"
            >
              <i
                class="fas"
                :class="allLocked ? 'fa-lock' : 'fa-unlock'"
              />
            </Button>
          </div>
          <!-- Ligne du bas: bottom-left et bottom-right -->
          <div class="corners-row">
            <BaseInputNumber
              v-model="leftInput.value"
              data-test-id="input-element-bottom-left"
              :suffix="SIGN"
              input-width="3.8rem"
              @input="handleInput(leftInput, leftInput.index)"
            />
            <BaseInputNumber
              v-model="bottomInput.value"
              data-test-id="input-element-bottom-right"
              :suffix="SIGN"
              input-width="3.8rem"
              @input="handleInput(bottomInput, bottomInput.index)"
            />
          </div>
        </div>

        <!-- Layout 4 valeurs pour marges/paddings (croix) -->
        <div
          v-else-if="inputList.length === 4"
          class="spatial-layout-4"
        >
          <!-- Haut (top) -->
          <div class="spatial-top">
            <BaseInputNumber
              v-model="topInput.value"
              data-test-id="input-element-top"
              :suffix="SIGN"
              input-width="3.8rem"
              @input="handleInput(topInput, topInput.index)"
            />
          </div>
          <!-- Milieu: Gauche - Cadenas - Droite -->
          <div class="spatial-middle">
            <BaseInputNumber
              v-model="leftInput.value"
              data-test-id="input-element-left"
              :suffix="SIGN"
              input-width="3.8rem"
              @input="handleInput(leftInput, leftInput.index)"
            />
            <Button
              v-tooltip.top="allLocked ? t('templateBuilder.fields.spaces.unlockValues') : t('templateBuilder.fields.spaces.lockValues')"
              data-test-id="lock-button-central"
              class="lock-button-central"
              :class="{ 'locked': allLocked }"
              @click="toggleAllLocked"
            >
              <i
                class="fas"
                :class="allLocked ? 'fa-lock' : 'fa-unlock'"
              />
            </Button>
            <BaseInputNumber
              v-model="rightInput.value"
              data-test-id="input-element-right"
              :suffix="SIGN"
              input-width="3.8rem"
              @input="handleInput(rightInput, rightInput.index)"
            />
          </div>
          <!-- Bas (bottom) -->
          <div class="spatial-bottom">
            <BaseInputNumber
              v-model="bottomInput.value"
              data-test-id="input-element-bottom"
              :suffix="SIGN"
              input-width="3.8rem"
              @input="handleInput(bottomInput, bottomInput.index)"
            />
          </div>
        </div>

        <!-- Layout 2 valeurs (marges verticales/horizontales) -->
        <div
          v-else-if="inputList.length === 2"
          class="spatial-layout-2"
        >
          <div class="spatial-2-item">
            <label class="input-label">{{ inputList[0].title }}</label>
            <BaseInputNumber
              v-model="inputList[0].value"
              data-test-id="input-element-first"
              :suffix="SIGN"
              input-width="3.8rem"
              @input="handleInput(inputList[0], 0)"
            />
          </div>
          <Button
            v-tooltip.top="allLocked ? t('templateBuilder.fields.spaces.unlockValues') : t('templateBuilder.fields.spaces.lockValues')"
            data-test-id="lock-button-central"
            class="lock-button-central"
            :class="{ 'locked': allLocked }"
            @click="toggleAllLocked"
          >
            <i
              class="fas"
              :class="allLocked ? 'fa-lock' : 'fa-unlock'"
            />
          </Button>
          <div class="spatial-2-item">
            <label class="input-label">{{ inputList[1].title }}</label>
            <BaseInputNumber
              v-model="inputList[1].value"
              data-test-id="input-element-second"
              :suffix="SIGN"
              input-width="3.8rem"
              @input="handleInput(inputList[1], 1)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent, PropType, ref, Ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';

import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';
import {
  FieldConfig, ParserFieldObject, Property, SpacesFieldOption,
} from '@/types';
import ToggleDisplaySettings from '@/components/template-builder/fields/partials/ToggleDisplaySettings.vue';

export default defineComponent({
  name: 'Spaces',
  components: {
    ToggleDisplaySettings,
    Button,
    BaseInputNumber,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },

    elementPartType: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const configs = ref(props.configs);
    const displayProperties = ref(!(!Object.prototype.hasOwnProperty.call(configs.value, 'displayToggle') || configs.value.displayToggle === true));
    const SIGN = ref('px');
    const baseProperty: Ref<string> = ref(props.configs.options.property);
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties);

    const buildInputList = (): SpacesFieldOption[] => Object.entries(props.configs.options).filter((data) => data[0] !== 'property')
      .map((data) => {
        const currentPropertyName = baseProperty.value === 'border-radius' ? `border-${data[0]}-radius` : `${baseProperty.value}-${data[0]}`;
        const parserValue = props.parserValues.properties.filter((property) => property.name === currentPropertyName)[0].value?.replace(new RegExp(SIGN.value, 'g'), '');
        const { defaultValue, title } = data[1];
        const value = !Number.isNaN(Number(parserValue)) ? Number(parserValue) : Number(defaultValue);
        return {
          title: t(title),
          property: currentPropertyName,
          defaultValue,
          value,
        };
      });
    const inputList: Ref<SpacesFieldOption[]> = ref(buildInputList());

    const checkIfAllValuesEqual = (): boolean => {
      if (inputList.value.length <= 1) return true;
      const firstValue = inputList.value[0].value;
      return inputList.value.every((item) => item.value === firstValue);
    };

    const allValuesEqual = checkIfAllValuesEqual();
    const advancedMode = ref(!allValuesEqual);
    const allLocked = ref(allValuesEqual);

    // Helper to find input by position name (top, bottom, left, right)
    // For border-radius: visual layout is top-left (top), top-right (right), bottom-right (bottom), bottom-left (left)
    const findInputByPosition = (position: string) => {
      let index = -1;

      if (baseProperty.value === 'border-radius') {
        // Border-radius uses corner names: top-left, top-right, bottom-left, bottom-right
        // Map visual positions to corner names
        const cornerMap: Record<string, string> = {
          top: 'top-left',
          right: 'top-right',
          bottom: 'bottom-right',
          left: 'bottom-left',
        };
        const corner = cornerMap[position];
        if (corner) {
          index = inputList.value.findIndex((item) => item.property.includes(`-${corner}-`));
        }
      } else {
        // Standard margin/padding: property ends with -top, -right, -bottom, -left
        index = inputList.value.findIndex((item) => item.property.endsWith(`-${position}`) || item.property.includes(`-${position}-`));
      }

      if (index !== -1) {
        return { ...inputList.value[index], index };
      }
      return { value: 0, property: '', defaultValue: 0, title: '', index: -1 };
    };

    const topInput = computed(() => findInputByPosition('top'));
    const bottomInput = computed(() => findInputByPosition('bottom'));
    const leftInput = computed(() => findInputByPosition('left'));
    const rightInput = computed(() => findInputByPosition('right'));

    const isBorderRadius = computed(() => baseProperty.value === 'border-radius');

    const unifiedValue = computed({
      get: () => inputList.value[0]?.value ?? 0,
      set: (val: number) => {
        inputList.value.forEach((item) => {
          item.value = val;
        });
      },
    });

    const toggleAdvancedMode = () => {
      advancedMode.value = !advancedMode.value;
      if (advancedMode.value) {
        allLocked.value = checkIfAllValuesEqual();
      }
    };

    const toggleAllLocked = () => {
      allLocked.value = !allLocked.value;
      if (allLocked.value) {
        const firstValue = inputList.value[0].value;
        inputList.value.forEach((item) => {
          item.value = firstValue;
        });
        const updatedProperties = properties.value.map((prop: Property) => {
          const updatedProperty = prop;
          updatedProperty.value = `${firstValue}${SIGN.value}`;
          return updatedProperty;
        });
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: updatedProperties,
        });
      }
    };

    const handleUnifiedInput = () => {
      const newValue = unifiedValue.value;
      inputList.value.forEach((item) => {
        item.value = newValue;
      });
      const updatedProperties = properties.value.map((prop: Property) => {
        const updatedProperty = prop;
        updatedProperty.value = `${newValue}${SIGN.value}`;
        return updatedProperty;
      });
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    const handleInput = (input: SpacesFieldOption, index: number) => {
      const { value, property, defaultValue } = input;
      const newValue: number = value === null ? Number(defaultValue) : value;

      if (allLocked.value) {
        inputList.value.forEach((item) => {
          item.value = newValue;
        });
        const updatedProperties = properties.value.map((prop: Property) => {
          const updatedProperty = prop;
          updatedProperty.value = `${newValue}${SIGN.value}`;
          return updatedProperty;
        });
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: updatedProperties,
        });
      } else {
        const updatedProperty: Property = properties.value.filter((prop: Property) => prop.name === property)[0];
        updatedProperty.value = `${newValue}${SIGN.value}`;
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [updatedProperty],
        });
        // Don't auto-lock when values become equal - let user control the lock manually
      }
    };

    return {
      t,
      inputList,
      SIGN,
      displayProperties,
      handleInput,
      advancedMode,
      allLocked,
      unifiedValue,
      toggleAdvancedMode,
      toggleAllLocked,
      handleUnifiedInput,
      topInput,
      bottomInput,
      leftInput,
      rightInput,
      isBorderRadius,
    };
  },
});
</script>

<style lang="scss" scoped>
.spaces-field-block {
  .input-label {
    font-size: 0.75rem;
    color: $mid-grey;
    margin-bottom: 0.25rem;
  }

  .advanced-toggle-btn {
    padding: 0.25rem 0.5rem;
    color: $mid-grey;

    &:hover {
      color: $brand-color-primary;
      background: transparent;
    }
  }

  .p-inputnumber {
    :deep() {
      .stepper-input {
        width: 100%;
        max-width: 6rem;
      }
    }
  }

  // Mode Simple
  .simple-mode {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0;
  }

  // Mode Avancé
  .advanced-mode {
    padding: 0.5rem 0;

    .advanced-mode-header {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 0.5rem;
    }
  }

  // Layout 4 coins pour border-radius
  .spatial-layout-corners {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;

    .corners-row {
      display: flex;
      justify-content: center;
      gap: 2rem;
    }

    .corners-center {
      display: flex;
      justify-content: center;
    }
  }

  // Layout 4 valeurs (disposition spatiale)
  .spatial-layout-4 {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;

    .spatial-top,
    .spatial-bottom {
      display: flex;
      justify-content: center;
    }

    .spatial-middle {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      width: 100%;
    }
  }

  // Layout 2 valeurs
  .spatial-layout-2 {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 1rem;

    .spatial-2-item {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }

  // Bouton cadenas central
  .lock-button-central {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    box-shadow: none !important;
    background-color: $brand-color-primary;
    border-color: $brand-color-primary;
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    min-width: 2rem;

    &:hover {
      background-color: #7aae26;
      border-color: #7aae26;
    }

    &:not(.locked) {
      background-color: $white;
      border-color: $heather;

      .fas {
        color: $heather;
      }

      &:hover {
        border-color: $brand-color-primary;

        .fas {
          color: $brand-color-primary;
        }
      }
    }

    .fas {
      color: white;
      font-size: 0.75rem;
      line-height: 1;
    }
  }
}
</style>
